import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, Download, Printer, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoice() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef();

  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  useEffect(() => {
    if (cancelled) {
      toast.error('Payment was cancelled');
    }
  }, [cancelled]);

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/invoice/${invoiceId}`);
      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Invoice not found');
      }
    } catch (err) {
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      const response = await fetch(`${API_URL}/api/public/invoice/${invoiceId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to start payment');
        setPaying(false);
      }
    } catch (err) {
      toast.error('Failed to start payment');
      setPaying(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const isPaid = invoice.status === 'paid' || invoice.balance_due <= 0;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      {/* Action Bar - Hidden in print */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice</h1>
          <p className="text-gray-600">#{invoice.invoice_number}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          {invoice.can_pay && (
            <Button onClick={handlePay} disabled={paying} className="bg-green-600 hover:bg-green-700">
              {paying ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Pay {formatCurrency(invoice.balance_due)}
            </Button>
          )}
        </div>
      </div>

      {/* Invoice Document */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none">
        {/* Header */}
        <div className={`px-8 py-6 ${isPaid ? 'bg-green-600' : 'bg-blue-600'} text-white`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{invoice.business.name}</h2>
              {invoice.business.email && (
                <p className="text-blue-100 mt-1">{invoice.business.email}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-blue-100 text-sm uppercase tracking-wider">Invoice</p>
              <p className="text-2xl font-bold">#{invoice.invoice_number}</p>
              {isPaid && (
                <span className="inline-flex items-center gap-1 mt-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="h-4 w-4" />
                  PAID
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="px-8 py-6 border-b">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Bill To</p>
              <p className="font-semibold text-gray-900">{invoice.client.name}</p>
              {invoice.client.email && <p className="text-gray-600">{invoice.client.email}</p>}
              {invoice.client.address && <p className="text-gray-600">{invoice.client.address}</p>}
            </div>
            <div className="text-right">
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Issue Date</p>
                <p className="text-gray-900">{formatDate(invoice.issue_date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Due Date</p>
                <p className="text-gray-900 font-semibold">{formatDate(invoice.due_date)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="px-8 py-6">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 text-xs text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-center py-3 text-xs text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="text-right py-3 text-xs text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="text-right py-3 text-xs text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-4 text-gray-900">{item.description}</td>
                  <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                  <td className="py-4 text-right text-gray-600">{formatCurrency(item.unit_price)}</td>
                  <td className="py-4 text-right text-gray-900 font-medium">{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-8 py-6 bg-gray-50">
          <div className="flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">{formatCurrency(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Tax</span>
                <span className="text-gray-900">{formatCurrency(invoice.tax)}</span>
              </div>
              <div className="flex justify-between py-2 border-t border-gray-300">
                <span className="text-gray-900 font-semibold">Total</span>
                <span className="text-gray-900 font-semibold">{formatCurrency(invoice.total)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between py-2 text-green-600">
                  <span>Amount Paid</span>
                  <span>-{formatCurrency(invoice.amount_paid)}</span>
                </div>
              )}
              <div className={`flex justify-between py-3 border-t-2 ${isPaid ? 'border-green-500' : 'border-blue-500'}`}>
                <span className="text-lg font-bold text-gray-900">Balance Due</span>
                <span className={`text-lg font-bold ${isPaid ? 'text-green-600' : 'text-blue-600'}`}>
                  {formatCurrency(invoice.balance_due)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="px-8 py-6 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Notes</p>
            <p className="text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Terms */}
        {invoice.terms && (
          <div className="px-8 py-6 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Terms & Conditions</p>
            <p className="text-gray-600 text-sm">{invoice.terms}</p>
          </div>
        )}

        {/* Pay Button - Print Hidden */}
        {invoice.can_pay && (
          <div className="px-8 py-6 bg-blue-50 border-t print:hidden">
            <div className="text-center">
              <Button 
                size="lg" 
                onClick={handlePay} 
                disabled={paying}
                className="bg-green-600 hover:bg-green-700 text-lg px-8"
              >
                {paying ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-5 w-5 mr-2" />
                )}
                Pay Now - {formatCurrency(invoice.balance_due)}
              </Button>
              <p className="text-sm text-gray-500 mt-2">Secure payment powered by Stripe</p>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
